import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Snackbar } from '@mui/material';
import type { TableVirtuosoHandle } from 'react-virtuoso';

import { WorkItem, WorkItemUpdate, workItemsApi, STATE_DONE, STATE_ARCHIVED, SanityCounts } from '../api/workItems';
import { isSourceHomeItem } from '../utils/sourceFamily';
import { AppliedSourceRule, itemMatchesStateFilter, patchWorkItemsForAppliedSourceRule } from './sourceTeachEligibility';
import { domainsApi, DomainConfig } from '../api/domains';
import { isRetiredMode } from '../modes/ModeLifecycleActions';
import { invalidatePageCache } from '../hooks/usePageCache';
import WorkItemDrawer from './WorkItemDrawer';
import KeyboardShortcutsModal from './KeyboardShortcutsModal';
import FileImportModal from './FileImportModal';
import { useConnectGmailModal } from '../context/ConnectGmailModalContext';
import { useTriageNavigation } from '../hooks/useTriageNavigation';
import { useDrawerNavigation } from '../hooks/useDrawerNavigation';
import { workbenchListPileLabel } from '../utils/drawerChromeCopy';
import {
    DOCUMENT_FAMILY_PILE_LABEL,
    dropDocumentFamilyScope,
    loadDocumentFamilySiblings,
    SOURCE_HOME_QUERY_PARAM,
} from '../utils/workbenchDocumentFamily';
import WorkItemsHeader from './WorkItemsHeader';
import WorkItemsMeta from './WorkItemsMeta';
import WorkItemsTable from './WorkItemsTable';
import ConfirmationDialog from './ConfirmationDialog';
import { BASE_AREAS, STATES, VIEWS, ViewKey } from './WorkItemsView.constants';
import { shouldInsertNewItem } from '../utils/workItemFilters';
import { useAuth } from '../auth/AuthContext';
import { useAppBootstrap } from '../context/AppBootstrapContext';
import OrganizeGeneralWorkbenchEntry from './OrganizeGeneralWorkbenchEntry';
import SimilarItemWorkbenchEntry from './SimilarItemWorkbenchEntry';
import { visuallyHiddenSx } from '../utils/visuallyHidden';
import { fetchSimilarItemSuggestions } from '../api/classificationSuggestions';
import {
    effectiveWorkbenchAreaFilter,
    itemDomainMatchesWorkbenchAreaFilter,
    normalizeAreaParam,
} from '../utils/domainPreference';

const DRAWER_ITEM_CACHE_KEY = 'workbench.drawerItem.v1';
const DRAWER_ITEM_CACHE_MAX_AGE_MS = 1000 * 60 * 30;
const LAST_FOCUS_ID_KEY = 'workbench.lastFocusId.v1';
const LAST_FOCUS_ITEM_KEY = 'workbench.lastFocusItem.v1';

/** Row root for focus return when the drawer opens without an explicit activator (Enter, restore, deep link). */
function getWorkbenchRowActivatorElement(itemId: string): HTMLElement | null {
    return document.querySelector(
        `[data-testid="WB_ROW"][data-item-id="${CSS.escape(itemId)}"]`
    ) as HTMLElement | null;
}

const PAGE1_STALE_MS = 60_000;
const pageOneCache = new Map<string, { items: WorkItem[]; hasMore: boolean; savedAt: number }>();

function getCachedPage1(key: string) {
    const entry = pageOneCache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.savedAt > 5 * 60_000) {
        pageOneCache.delete(key);
        return null;
    }
    return entry;
}

function setCachedPage1(key: string, items: WorkItem[], hasMore: boolean) {
    pageOneCache.set(key, { items, hasMore, savedAt: Date.now() });
}

/** Clears in-memory Workbench page-1 cache (used by tests for deterministic runs). */
export function clearWorkbenchPageOneCache(): void {
    pageOneCache.clear();
}

interface WorkItemsViewProps {
    initialView?: ViewKey;
    showHeader?: boolean;
    forcedState?: string;
}

const WorkItemsView: React.FC<WorkItemsViewProps> = ({ initialView, showHeader = true, forcedState: propForcedState }) => {
    const { session } = useAuth();
    const { bootstrap, loading: bootstrapLoading } = useAppBootstrap();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const isArchiveView = propForcedState === STATE_ARCHIVED;

    // View Management
    const viewParamFromUrl = searchParams.get('view');
    const requestedView = (viewParamFromUrl || initialView || 'all') as ViewKey;
    const currentView = (VIEWS.some((v) => v.id === requestedView) ? requestedView : 'all') as ViewKey;
    const areaParamFromUrl = searchParams.get('area');
    const entityFilterFromUrl = searchParams.get('entity') || null;
    const implicitArea = normalizeAreaParam(areaParamFromUrl) === null;
    const deferImplicitDomain =
        implicitArea &&
        !!session &&
        bootstrap === null &&
        bootstrapLoading;
    const areaExplicitUserFilter = normalizeAreaParam(areaParamFromUrl) !== null;
    // Insights job-hunt deep links intentionally omit `area`; keep them cross-domain on first load.
    const preserveApplicationsDeepLinkScope =
        !areaExplicitUserFilter &&
        entityFilterFromUrl !== null &&
        viewParamFromUrl === 'applications';
    const areaFilter = preserveApplicationsDeepLinkScope
        ? 'all'
        : effectiveWorkbenchAreaFilter(
            areaParamFromUrl,
            bootstrap?.active_domain_key ?? bootstrap?.user?.active_domain_key,
            { deferImplicitUntilBootstrap: deferImplicitDomain },
        );
    const stateFilterFromUrl = searchParams.get('state');
    // Default to 'ACTIVE,TRIAGE' so new items aren't hidden from the main list.
    const stateFilter = stateFilterFromUrl || 'ACTIVE,TRIAGE';
    const needsActionOnly = searchParams.get('needsAction') === 'true';
    const overdueOnly = searchParams.get('overdue') === 'true';
    const focusIdFromUrl = searchParams.get('focusId');
    const sourceHomeId = (searchParams.get(SOURCE_HOME_QUERY_PARAM) || '').trim();
    const qFromUrl = searchParams.get('q') ?? '';

    const [items, setItems] = useState<WorkItem[]>([]);
    const [searchTotal, setSearchTotal] = useState<number | null>(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [documentFamilyList, setDocumentFamilyList] = useState(false);
    const documentFamilyListRef = useRef(false);
    documentFamilyListRef.current = documentFamilyList;
    // The URL scope, not the last fetch result: a failed family load still means
    // "This document", so an import must not land under its error.
    const sourceHomeIdRef = useRef(sourceHomeId);
    sourceHomeIdRef.current = sourceHomeId;
    const [sanityCounts, setSanityCounts] = useState<SanityCounts | null>(null);
    const [domainConfigs, setDomainConfigs] = useState<DomainConfig[]>([]);
    const [includeRetiredModes, setIncludeRetiredModes] = useState(false);
    const [similarItemSuggestionCount, setSimilarItemSuggestionCount] = useState(0);
    const lastFetchKeyRef = useRef<string>('');

    // Filters State — synced with `q` query param (debounced on typing)
    const [search, setSearch] = useState(qFromUrl);
    const searchUrlDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { openModal: openConnectGmailModal } = useConnectGmailModal();

    useEffect(() => {
        if (searchUrlDebounceRef.current) {
            clearTimeout(searchUrlDebounceRef.current);
            searchUrlDebounceRef.current = null;
        }
        setSearch(qFromUrl);
        setSearchTotal(null);
    }, [qFromUrl]);

    useEffect(() => {
        return () => {
            if (searchUrlDebounceRef.current) clearTimeout(searchUrlDebounceRef.current);
        };
    }, []);

    useEffect(() => {
        if (bootstrapLoading || bootstrap?.capabilities?.similar_item_suggestions_enabled !== true) {
            setSimilarItemSuggestionCount(0);
            return;
        }
        let cancelled = false;
        void fetchSimilarItemSuggestions()
            .then((response) => {
                if (!cancelled) {
                    setSimilarItemSuggestionCount(response.total);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setSimilarItemSuggestionCount(0);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [
        bootstrap?.capabilities?.similar_item_suggestions_enabled,
        bootstrapLoading,
        location.pathname,
    ]);

    const flushSearchToUrl = useCallback((value: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (value.trim()) next.set('q', value);
            else next.delete('q');
            return dropDocumentFamilyScope(next);
        }, { replace: true });
    }, [setSearchParams]);

    const handleSearchChange = useCallback((value: string) => {
        setSearch(value);
        setSearchTotal(null);
        if (searchUrlDebounceRef.current) clearTimeout(searchUrlDebounceRef.current);
        searchUrlDebounceRef.current = setTimeout(() => {
            searchUrlDebounceRef.current = null;
            flushSearchToUrl(value);
        }, 350);
    }, [flushSearchToUrl]);

    const clearSearchUrlImmediate = useCallback(() => {
        if (searchUrlDebounceRef.current) {
            clearTimeout(searchUrlDebounceRef.current);
            searchUrlDebounceRef.current = null;
        }
        setSearch('');
        setSearchTotal(null);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('q');
            return dropDocumentFamilyScope(next);
        }, { replace: true });
    }, [setSearchParams]);
    const [selected, setSelected] = useState<WorkItem | null>(null);
    const [showShortcuts, setShowShortcuts] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' | 'info' } | null>(null);
    const [lastAction, setLastAction] = useState<{
        items: WorkItem[];
        previousStates: Record<string, string>;
    } | null>(null);
    const virtuosoRef = useRef<TableVirtuosoHandle | null>(null);
    const drawerActivatorRef = useRef<HTMLElement | null>(null);
    const isMountedRef = useRef(true);
    const pendingActionTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [retryToken, setRetryToken] = useState(0);
    const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; ids: string[]; isBulk: boolean }>({
        open: false,
        ids: [],
        isBulk: false,
    });
    const suppressAutoRestoreUntilRef = useRef<number>(0);

    const setFocusIdParam = useCallback((id: string | null) => {
        if ((focusIdFromUrl ?? null) === id) return;
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (id) {
                next.set('focusId', id);
            } else {
                next.delete('focusId');
            }
            return next;
        }, { replace: true });
    }, [focusIdFromUrl, setSearchParams]);

    useEffect(() => {
        if (!focusIdFromUrl || selected) return;

        try {
            const raw = sessionStorage.getItem(DRAWER_ITEM_CACHE_KEY);
            if (!raw) return;

            const parsed = JSON.parse(raw) as { item?: WorkItem; savedAt?: number };
            if (!parsed?.item || parsed.item.id !== focusIdFromUrl) return;

            if (typeof parsed.savedAt === 'number' && Date.now() - parsed.savedAt > DRAWER_ITEM_CACHE_MAX_AGE_MS) {
                sessionStorage.removeItem(DRAWER_ITEM_CACHE_KEY);
                return;
            }

            drawerActivatorRef.current = getWorkbenchRowActivatorElement(parsed.item.id);
            setSelected(parsed.item);
        } catch {
            // Ignore malformed session cache.
        }
    }, [focusIdFromUrl, selected]);

    useEffect(() => {
        if (focusIdFromUrl || selected) return;
        if (Date.now() < suppressAutoRestoreUntilRef.current) return;

        try {
            const raw = sessionStorage.getItem(LAST_FOCUS_ITEM_KEY);
            if (!raw) return;
            const cached = JSON.parse(raw) as WorkItem | null;
            if (!cached?.id) return;

            drawerActivatorRef.current = getWorkbenchRowActivatorElement(cached.id);
            setSelected(cached);
            setFocusIdParam(cached.id);
        } catch {
            // Ignore malformed session cache.
        }
    }, [focusIdFromUrl, selected, setFocusIdParam]);

    useEffect(() => {
        try {
            if (!selected) {
                sessionStorage.removeItem(DRAWER_ITEM_CACHE_KEY);
                sessionStorage.removeItem(LAST_FOCUS_ID_KEY);
                sessionStorage.removeItem(LAST_FOCUS_ITEM_KEY);
                return;
            }

            sessionStorage.setItem(
                DRAWER_ITEM_CACHE_KEY,
                JSON.stringify({
                    savedAt: Date.now(),
                    item: selected,
                })
            );
            sessionStorage.setItem(LAST_FOCUS_ID_KEY, selected.id);
            sessionStorage.setItem(LAST_FOCUS_ITEM_KEY, JSON.stringify(selected));
        } catch {
            // Ignore sessionStorage failures.
        }
    }, [selected]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const refreshSanityCounts = useCallback(async () => {
        try {
            const counts = await workItemsApi.getSanityCounts();
            if (isMountedRef.current) {
                setSanityCounts(counts);
            }
        } catch (e) {
            console.warn('Failed to fetch sanity counts', e);
        }
    }, []);

    const domainKeyForArea = (domain: DomainConfig) => {
        const key = (domain.key || '').trim();
        return key === 'work' ? 'business' : key;
    };

    useEffect(() => {
        let mounted = true;

        const loadDomainConfigs = async () => {
            try {
                const activeDomains = await domainsApi.getDomains();
                if (!mounted) return;

                const baseAreaIds = new Set(BASE_AREAS.map((area) => area.id));
                const areaTargetsRetiredCandidate =
                    !!areaFilter
                    && areaFilter !== 'all'
                    && !baseAreaIds.has(areaFilter)
                    && !activeDomains.some((domain) => domainKeyForArea(domain) === areaFilter);

                const shouldIncludeRetired = includeRetiredModes || areaTargetsRetiredCandidate;
                const domains = shouldIncludeRetired
                    ? await domainsApi.getDomains({ includeRetired: true })
                    : activeDomains;

                if (!mounted) return;
                setDomainConfigs(domains);

                if (
                    !includeRetiredModes
                    && areaFilter
                    && areaFilter !== 'all'
                    && domains.some(
                        (domain) => domainKeyForArea(domain) === areaFilter && isRetiredMode(domain),
                    )
                ) {
                    setIncludeRetiredModes(true);
                }
            } catch {
                if (mounted) setDomainConfigs([]);
            }
        };

        void loadDomainConfigs();
        return () => {
            mounted = false;
        };
    }, [includeRetiredModes, areaFilter]);

    const areas = (() => {
        const map = new Map<string, { id: string; label: string }>();
        for (const a of BASE_AREAS) map.set(a.id, a);

        const areaDomainConfigs = includeRetiredModes
            ? domainConfigs
            : domainConfigs.filter((domain) => !isRetiredMode(domain));

        for (const d of areaDomainConfigs) {
            const key = (d.key || '').trim();
            if (!key) continue;
            const normalizedKey = key === 'work' ? 'business' : key;
            const existing = map.get(normalizedKey);
            const retiredSuffix = d.lifecycle_status === 'archived'
                ? ' (archived)'
                : d.lifecycle_status === 'deactivated'
                    ? ' (inactive)'
                    : '';
            map.set(normalizedKey, {
                id: normalizedKey,
                label: `${(d.label || existing?.label || normalizedKey).trim()}${retiredSuffix}`,
            });
        }

        const baseOrder = BASE_AREAS.map((a) => a.id);
        const baseItems = baseOrder.map((id) => map.get(id)).filter(Boolean) as { id: string; label: string }[];
        const extraItems = Array.from(map.values()).filter((x) => !baseOrder.includes(x.id));
        extraItems.sort((a, b) => a.label.localeCompare(b.label));
        return [...baseItems, ...extraItems];
    })();

    const handleIncludeRetiredModesChange = (checked: boolean) => {
        setIncludeRetiredModes(checked);
        if (checked) return;

        const activeAreaIds = new Set([
            ...BASE_AREAS.map((area) => area.id),
            ...domainConfigs
                .filter((domain) => !isRetiredMode(domain))
                .map((domain) => ((domain.key || '').trim() === 'work' ? 'business' : (domain.key || '').trim()))
                .filter(Boolean),
        ]);
        if (areaFilter && !activeAreaIds.has(areaFilter)) {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('area', 'all');
                return dropDocumentFamilyScope(next);
            });
        }
    };

    const views = useMemo(() => VIEWS, []);

    // Handle deep-linking focusId (select + scroll) without an arbitrary delay.
    const deepLinkScrollAppliedRef = useRef<string | null>(null);
    useEffect(() => {
        if (!focusIdFromUrl) {
            deepLinkScrollAppliedRef.current = null;
            return;
        }

        if (deepLinkScrollAppliedRef.current === focusIdFromUrl) return;
        if (!virtuosoRef.current) return;

        const index = items.findIndex((i) => i.id === focusIdFromUrl);
        if (index < 0) return;

        deepLinkScrollAppliedRef.current = focusIdFromUrl;

        const item = items[index];
        if (selected?.id !== item.id) {
            drawerActivatorRef.current = getWorkbenchRowActivatorElement(item.id);
            setSelected(item);
        }

        let raf1: number | null = null;
        let raf2: number | null = null;

        raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                virtuosoRef.current?.scrollToIndex({ index, align: 'center', behavior: 'smooth' });
            });
        });

        return () => {
            if (raf1 !== null) cancelAnimationFrame(raf1);
            if (raf2 !== null) cancelAnimationFrame(raf2);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusIdFromUrl, items, loading]);

    // The source home is not one of the N tasks. Open in Workbench still focuses it
    // while the list stays the siblings the drawer named.
    useEffect(() => {
        if (!sourceHomeId || !documentFamilyList || loading || !focusIdFromUrl) return;
        if (Date.now() < suppressAutoRestoreUntilRef.current) return;
        if (selected?.id === focusIdFromUrl) return;
        if (items.some((row) => row.id === focusIdFromUrl)) return;
        let cancelled = false;
        workItemsApi.getWorkItem(focusIdFromUrl)
            .then((row) => {
                if (cancelled || !row || row.id !== focusIdFromUrl) return;
                drawerActivatorRef.current = getWorkbenchRowActivatorElement(row.id);
                setSelected(row);
            })
            .catch(() => {
                // The sibling rows are the result. A missing focus item does not blank them.
            });
        return () => {
            cancelled = true;
        };
    }, [sourceHomeId, documentFamilyList, loading, focusIdFromUrl, items, selected?.id]);

    // Resets when filters change (URL or local search)
    useEffect(() => {
        setPage(1);
        setHasMore(true);
        setSearchTotal(null);
        // We don't clear items here to avoid "flicker";fetchItems(1) will replace them.
    }, [currentView, areaFilter, search, entityFilterFromUrl, stateFilter, needsActionOnly, overdueOnly, propForcedState, sourceHomeId]);

    const fetchItems = async (currentPage: number, abortController: AbortController) => {
        if (sourceHomeId) {
            setLoading(true);
            setFetchError(null);
            try {
                const loaded = await loadDocumentFamilySiblings(sourceHomeId);
                if (abortController.signal.aborted) return;
                if (loaded.status === 'not-document-family') {
                    setDocumentFamilyList(false);
                    setSearchParams((prev) => {
                        if (!prev.get(SOURCE_HOME_QUERY_PARAM)) return prev;
                        const next = new URLSearchParams(prev);
                        next.delete(SOURCE_HOME_QUERY_PARAM);
                        return next;
                    }, { replace: true });
                    return;
                }
                setItems(loaded.items);
                setHasMore(false);
                setSearchTotal(null);
                setDocumentFamilyList(true);
                setCachedPage1(lastFetchKeyRef.current, loaded.items, false);
            } catch (e) {
                if (!abortController.signal.aborted) {
                    console.error('Fetch items failed', e);
                    setDocumentFamilyList(false);
                    setItems([]);
                    setFetchError('Could not load work items. Please try again.');
                    setSearchTotal(null);
                }
            } finally {
                if (!abortController.signal.aborted) setLoading(false);
            }
            return;
        }
        setDocumentFamilyList(false);
        if (areaFilter === null) {
            setLoading(true);
            return;
        }
        setLoading(true);
        setFetchError(null);
        try {
            // Build Params based on current view context
            let typeParam: string | undefined = undefined;
            let domainParam: string | undefined = areaFilter !== 'all' ? areaFilter : undefined;
            let stateParam: string | undefined = propForcedState || (stateFilter === 'ALL' ? undefined : stateFilter);
            let needsActionParam: boolean | undefined = needsActionOnly ? true : undefined;

            // 1. Context-Aware Filtering Logic
            if (currentView === 'inbox') {
                if (areaFilter === 'job_hunt') {
                    typeParam = 'INBOX,APPLICATION,TASK';
                } else if (areaFilter !== 'all') {
                    typeParam = 'INBOX,TASK';
                } else {
                    typeParam = 'INBOX';
                }
            } else if (currentView === 'applications') {
                typeParam = 'APPLICATION';
                // Domain-neutral: show APPLICATION items across all domains.
                // Users can scope to job_hunt via the Area filter if needed.
            } else if (currentView === 'tasks') {
                typeParam = 'TASK';
            }

            const data = await workItemsApi.getWorkItems({
                page: currentPage,
                limit: 50,
                type: typeParam,
                domain: domainParam,
                state: stateParam,
                company: entityFilterFromUrl || undefined,
                search: search || undefined,
                needs_action: needsActionParam,
                sort_by: overdueOnly ? 'due_date' : 'last_activity_at',
                sort_order: overdueOnly ? 'asc' : 'desc',
            }, { signal: abortController.signal });

            if (abortController.signal.aborted) return;

            // Client-side overdue filter: only show items whose due_date is before today
            let filtered = data.items;
            if (overdueOnly) {
                const todayStr = new Date().toISOString().slice(0, 10);
                filtered = data.items.filter((item) => {
                    if (!item.due_date) return false;
                    const dueDateStr = typeof item.due_date === 'string'
                        ? item.due_date.slice(0, 10)
                        : new Date(item.due_date).toISOString().slice(0, 10);
                    return dueDateStr < todayStr;
                });
            }

            const nextHasMore = data.items.length === 50;
            if (currentPage === 1) {
                setItems(filtered);
                setCachedPage1(lastFetchKeyRef.current, filtered, nextHasMore);
                setSearchTotal(
                    search.trim() && !overdueOnly ? data.total : null,
                );
            } else {
                setItems((prev) => [...prev, ...filtered]);
            }

            setHasMore(nextHasMore);
        } catch (e) {
            if (!abortController.signal.aborted) {
                console.error('Fetch items failed', e);
                setFetchError('Could not load work items. Please try again.');
                setSearchTotal(null);
            }
        } finally {
            if (!abortController.signal.aborted) setLoading(false);
        }
    };

    useEffect(() => {
        if (!sourceHomeId && areaFilter === null) {
            setLoading(true);
            return () => {};
        }
        const fetchKey = sourceHomeId
            ? `document-family|${sourceHomeId}`
            : [
                currentView,
                areaFilter,
                search,
                entityFilterFromUrl || '',
                stateFilter,
                needsActionOnly ? '1' : '0',
                propForcedState || '',
            ].join('|');

        if (page !== 1 && lastFetchKeyRef.current && lastFetchKeyRef.current !== fetchKey) {
            return;
        }

        lastFetchKeyRef.current = fetchKey;

        if (page === 1 && retryToken === 0) {
            const cached = getCachedPage1(fetchKey);
            if (cached) {
                setItems(cached.items);
                setHasMore(cached.hasMore);
                setDocumentFamilyList(Boolean(sourceHomeId));
                const isFresh = Date.now() - cached.savedAt < PAGE1_STALE_MS;
                if (isFresh) {
                    setLoading(false);
                    return;
                }
            }
        }

        const controller = new AbortController();
        fetchItems(page, controller);
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, currentView, areaFilter, search, entityFilterFromUrl, stateFilter, needsActionOnly, overdueOnly, propForcedState, retryToken, sourceHomeId]);

    // Fetch sanity counts for the trust bar (once on mount; refresh explicitly after mutations).
    useEffect(() => {
        refreshSanityCounts();
    }, [refreshSanityCounts]);

    // Load More Handler
    function loadMore() {
        if (!loading && hasMore && areaFilter !== null) {
            setPage((prev) => prev + 1);
        }
    }



    const restoreDrawerActivatorFocus = useCallback(() => {
        const el = drawerActivatorRef.current;
        drawerActivatorRef.current = null;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (el && document.contains(el)) {
                    el.focus({ preventScroll: true });
                } else {
                    const main = document.querySelector('main');
                    if (main instanceof HTMLElement) {
                        main.focus({ preventScroll: true });
                    }
                }
            });
        });
    }, []);

    const openItem = (item: WorkItem, activator?: HTMLElement | null) => {
        drawerActivatorRef.current = activator ?? getWorkbenchRowActivatorElement(item.id);
        setSelected(item);
        setFocusIdParam(item.id);
    };

    // Drawer prev/next + auto-advance follow the current page's visible order.
    const workbenchPileLabel = useMemo(
        () => (sourceHomeId && documentFamilyList
            ? DOCUMENT_FAMILY_PILE_LABEL
            : workbenchListPileLabel({
                view: currentView,
                stateFilter: propForcedState || stateFilter,
                archive: isArchiveView,
            })),
        [sourceHomeId, documentFamilyList, currentView, isArchiveView, propForcedState, stateFilter],
    );

    const drawerNavigation = useDrawerNavigation(items, selected?.id ?? null, (id: string) => {
        const target = items.find((candidate) => candidate.id === id);
        if (!target) return false;
        openItem(target);
        return true;
    });
    const drawerNavigationWithPile = useMemo(
        () => (drawerNavigation ? { ...drawerNavigation, pileLabel: workbenchPileLabel } : undefined),
        [drawerNavigation, workbenchPileLabel],
    );

    const handleImportItems = async (drafts: WorkItemUpdate[]) => {
        setShowImportModal(false);
        if (drafts.length === 0) return;

        try {
            const results = await Promise.allSettled(
                drafts.map(d => workItemsApi.createWorkItem(d))
            );

            const succeeded = results
                .filter((r): r is PromiseFulfilledResult<WorkItem> => r.status === 'fulfilled')
                .map(r => r.value);

            const failedCount = drafts.length - succeeded.length;

            if (succeeded.length > 0) {
                // This document is the drawer's siblings. An import from elsewhere
                // is not one of them — keep creating the items, but do not prepend.
                if (!documentFamilyListRef.current && !sourceHomeIdRef.current) {
                    setItems(prev => {
                        const toAdd = succeeded.filter(s =>
                            shouldInsertNewItem(s, { currentView, areaFilter, stateFilter, needsActionOnly }) &&
                            !prev.some(it => it.id === s.id)
                        );
                        return [...toAdd, ...prev];
                    });
                }
                refreshSanityCounts();
                setToast({
                    message: `Successfully created ${succeeded.length} item${succeeded.length === 1 ? '' : 's'}.`,
                    severity: 'success'
                });
            }

            if (failedCount > 0) {
                showErrorToast(`Failed to create ${failedCount} item${failedCount === 1 ? '' : 's'}.`);
            }
        } catch (err) {
            console.error(err);
            showErrorToast('Batch import failed. Please try again.');
        }
    };


    const invalidateCaches = useCallback(() => {
        invalidatePageCache('today');
        pageOneCache.clear();
    }, []);

    const handleItemUpdate = (updated: WorkItem, sourceRule?: AppliedSourceRule) => {
        setItems((prev) => {
            const patched = patchWorkItemsForAppliedSourceRule(prev, updated, sourceRule);
            // The document list is the drawer's siblings, including a step that just left Open.
            if (documentFamilyListRef.current) return patched;
            const visible = patched.filter((item) => itemMatchesStateFilter(item, stateFilter));
            // Undo after the drawer auto-advanced brings a filtered-out item back into view.
            if (!visible.some((item) => item.id === updated.id) && itemMatchesStateFilter(updated, stateFilter)) {
                return [updated, ...visible];
            }
            return visible;
        });
        // The drawer may already be on another item (auto-advance); only refresh it for a match.
        setSelected((prev) => (prev && prev.id === updated.id ? updated : prev));
        refreshSanityCounts();
        invalidateCaches();
        // Recheck server-owned search, domain, type and attention filters after edits.
        // Keep drawer selection independent so this cannot undo automatic navigation.
        setPage(1);
        setRetryToken((prev) => prev + 1);
    };

    const handleItemDelete = (id: string) => {
        setItems((prev) => prev.filter((it) => it.id !== id));
        setSelected(null);
        refreshSanityCounts();
        invalidateCaches();
    };

    const showErrorToast = (message: string) => {
        setToast({ message, severity: 'error' });
    };

    // Quick Actions
    const quickDone = async (item: WorkItem, e: React.MouseEvent) => {
        e.stopPropagation();
        if (isSourceHomeItem(item)) return;
        try {
            const updated = await workItemsApi.updateWorkItem(item.id, { state: STATE_DONE, needs_action: false });
            setLastAction({
                items: [item],
                previousStates: { [item.id]: item.state || 'ACTIVE' }
            });
            setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
            setToast({ message: 'Item marked as done.', severity: 'success' });
            refreshSanityCounts();
        } catch (err) {
            console.error(err);
            showErrorToast('Could not mark item as done. Please try again.');
        }
    };

    const quickSnooze = async (item: WorkItem, days: number, e: React.MouseEvent) => {
        e.stopPropagation();
        const date = new Date();
        date.setDate(date.getDate() + days);
        try {
            const updated = await workItemsApi.updateWorkItem(item.id, { snoozed_until: date.toISOString(), needs_action: false });
            setLastAction({
                items: [item],
                previousStates: { [item.id]: item.state || 'ACTIVE' }
            });
            setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
            setToast({ message: `Snoozed for ${days} day${days === 1 ? '' : 's'}.`, severity: 'success' });
            refreshSanityCounts();
        } catch (err) {
            console.error(err);
            showErrorToast('Could not snooze item. Please try again.');
        }
    };

    const quickArchive = async (item: WorkItem, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            const updated = await workItemsApi.updateWorkItem(item.id, { state: STATE_ARCHIVED, needs_action: false });
            setLastAction({
                items: [item],
                previousStates: { [item.id]: item.state || 'ACTIVE' }
            });
            setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
            setToast({ message: 'Item archived.', severity: 'success' });
            refreshSanityCounts();
        } catch (err) {
            console.error(err);
            showErrorToast('Could not archive item. Please try again.');
        }
    };

    const quickDelete = (item: WorkItem, e: React.MouseEvent) => {
        e.stopPropagation();
        setConfirmDelete({ open: true, ids: [item.id], isBulk: false });
    };

    const handleReclassify = async (item: WorkItem, domain: string) => {
        try {
            const updated = await workItemsApi.updateWorkItem(item.id, { domain });

            const shouldRemoveFromView =
                !documentFamilyListRef.current &&
                areaFilter != null &&
                areaFilter !== 'all' &&
                !itemDomainMatchesWorkbenchAreaFilter(updated.domain, areaFilter);

            // Auto-filter: Remove item if it no longer matches the current view's domain scope
            // (explicit area chip or implicit active-domain default from bootstrap)
            if (shouldRemoveFromView) {
                setItems(prev => prev.filter(it => it.id !== item.id));
                setToast({ message: `Reclassified to ${domain} (moved out of view).`, severity: 'success' });
            } else {
                setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
                setToast({ message: `Reclassified to ${domain}.`, severity: 'success' });
            }

            if (selected?.id === item.id) setSelected(updated);
            refreshSanityCounts();
        } catch (err) {
            console.error(err);
            showErrorToast('Reclassification failed.');
        }
    };

    const performDelete = async () => {
        const { ids } = confirmDelete;
        if (ids.length === 0) return;

        try {
            const results = await Promise.allSettled(ids.map((id) => workItemsApi.deleteWorkItem(id)));
            const succeededIds = ids.filter((_, index) => results[index].status === 'fulfilled');
            const failedCount = ids.length - succeededIds.length;

            if (succeededIds.length > 0) {
                setItems((prev) => prev.filter((it) => !succeededIds.includes(it.id)));
                if (selected?.id && succeededIds.includes(selected.id)) {
                    setSelected(null);
                }
                setToast({
                    message: succeededIds.length === 1 ? 'Item deleted.' : `Deleted ${succeededIds.length} items.`,
                    severity: 'success'
                });
                refreshSanityCounts();
                invalidateCaches();
            }
            if (failedCount > 0) {
                showErrorToast(`${failedCount} item${failedCount === 1 ? '' : 's'} failed to delete.`);
            }
        } catch (e) {
            console.error(e);
            showErrorToast('Delete failed. Please try again.');
        } finally {
            setConfirmDelete({ open: false, ids: [], isBulk: false });
        }
    };

    const handleBulkDone = async (ids: string[]) => {
        const completableIds = ids.filter((id) => {
            const match = items.find((row) => row.id === id);
            return match ? !isSourceHomeItem(match) : true;
        });
        if (completableIds.length === 0) return;
        ids = completableIds;
        // Optimistic UI Update
        const previousStates = Object.fromEntries(
            items.filter(it => ids.includes(it.id)).map(it => [it.id, it.state || 'ACTIVE'])
        );
        const originalItems = [...items];

        setItems(prev => prev.map(it => ids.includes(it.id) ? { ...it, state: STATE_DONE, needs_action: false } : it));
        refreshSanityCounts();

        setLastAction({
            items: items.filter(it => ids.includes(it.id)),
            previousStates
        });

        setToast({ message: `Marking ${ids.length} items as done...`, severity: 'info' });

        if (pendingActionTimerRef.current) clearTimeout(pendingActionTimerRef.current);

        pendingActionTimerRef.current = setTimeout(async () => {
            try {
                const result = await workItemsApi.bulkUpdateWorkItems(ids, { state: STATE_DONE, needs_action: false });

                if (result.failed_count > 0) {
                    // Since we don't know exactly which failed without more logic, we revert all for safety if any fail? 
                    // Or just trust the successful IDs? 
                    // The endpoint returns updated_ids, so we can keep those and revert others if needed.
                    // But for simplicity, if failed_count > 0, let's warn.
                    showErrorToast(`${result.failed_count} items failed to update.`);
                    // We could revert the optimistic update for items NOT in updated_ids
                    const succeededSet = new Set(result.updated_ids);
                    setItems(prev => prev.map(it =>
                        ids.includes(it.id) && !succeededSet.has(it.id)
                            ? { ...it, state: originalItems.find(o => o.id === it.id)?.state || 'ACTIVE' }
                            : it
                    ));
                } else {
                    setToast({ message: `Done: ${result.updated_count} items.`, severity: 'success' });
                }
            } catch (e) {
                console.error(e);
                showErrorToast('Bulk update failed.');
                setItems(originalItems);
            } finally {
                pendingActionTimerRef.current = null;
            }
        }, 4000);
    };

    const handleBulkArchive = async (ids: string[]) => {
        // Optimistic UI Update
        const previousStates = Object.fromEntries(
            items.filter(it => ids.includes(it.id)).map(it => [it.id, it.state || 'ACTIVE'])
        );
        const originalItems = [...items];

        setItems(prev => prev.map(it => ids.includes(it.id) ? { ...it, state: STATE_ARCHIVED, needs_action: false } : it));
        refreshSanityCounts();

        setLastAction({
            items: items.filter(it => ids.includes(it.id)),
            previousStates
        });

        setToast({ message: `Archiving ${ids.length} items...`, severity: 'info' });

        if (pendingActionTimerRef.current) clearTimeout(pendingActionTimerRef.current);

        pendingActionTimerRef.current = setTimeout(async () => {
            try {
                const result = await workItemsApi.bulkUpdateWorkItems(ids, { state: STATE_ARCHIVED, needs_action: false });

                if (result.failed_count > 0) {
                    showErrorToast(`${result.failed_count} items failed to archive.`);
                    const succeededSet = new Set(result.updated_ids);
                    setItems(prev => prev.map(it =>
                        ids.includes(it.id) && !succeededSet.has(it.id)
                            ? { ...it, state: originalItems.find(o => o.id === it.id)?.state || 'ACTIVE' }
                            : it
                    ));
                } else {
                    setToast({ message: `Archived ${result.updated_count} items.`, severity: 'success' });
                }
            } catch (e) {
                console.error(e);
                showErrorToast('Bulk archive failed.');
                setItems(originalItems);
            } finally {
                pendingActionTimerRef.current = null;
            }
        }, 4000);
    };

    const handleBulkDelete = (ids: string[]) => {
        setConfirmDelete({ open: true, ids, isBulk: true });
    };

    const quickArchiveOrDelete = isArchiveView ? quickDelete : quickArchive;
    const bulkArchiveOrDelete = isArchiveView ? handleBulkDelete : handleBulkArchive;

    const handleBulkSnooze = async (ids: string[]) => {
        // Default 1 day for keyboard shortcut
        const date = new Date();
        date.setDate(date.getDate() + 1);
        try {
            const iso = date.toISOString();
            const result = await workItemsApi.bulkUpdateWorkItems(ids, { snoozed_until: iso, needs_action: false });

            const succeededIds = result.updated_ids;
            const failedCount = result.failed_count;

            if (succeededIds.length > 0) {
                const succeededItems = items.filter(it => succeededIds.includes(it.id));
                setLastAction({
                    items: succeededItems,
                    previousStates: Object.fromEntries(succeededItems.map(it => [it.id, it.state || 'ACTIVE']))
                });
                setItems((prev) => prev.map((it) => (succeededIds.includes(it.id) ? { ...it, snoozed_until: iso, needs_action: false } : it)));
                setToast({ message: `Snoozed ${succeededIds.length} items.`, severity: 'success' });
                refreshSanityCounts();
            }
            if (failedCount > 0) {
                showErrorToast(`${failedCount} item${failedCount === 1 ? '' : 's'} failed to update. Please retry.`);
            }
        } catch (e) {
            console.error(e);
            showErrorToast('Bulk update failed. Please try again.');
        }
    };

    const {
        focusedId,
        selectedIds,
        handleItemClick,
        handleItemDoubleClick,
        handleCheckboxClick,
        setSelectedIds,
        focusSource,
        setFocusSource
    } = useTriageNavigation({
        items: items,
        onOpen: openItem,
        onDone: handleBulkDone,
        onArchive: bulkArchiveOrDelete,
        onSnooze: handleBulkSnooze,
        shortcutsEnabled: !selected,
    });

    // Sync Scroll with Keyboard Focus
    useEffect(() => {
        if (focusedId && focusSource === 'keyboard' && virtuosoRef.current) {
            const index = items.findIndex((it) => it.id === focusedId);
            if (index >= 0) {
                // scrollToIndex is more robust for virtualized lists than scrollIntoView
                // Using 'auto' instead of 'smooth' to prevent jank during rapid keyboard nav
                virtuosoRef.current.scrollToIndex({
                    index,
                    align: 'center',
                    behavior: 'auto'
                });
            }
            // Reset focus source so future item updates don't trigger re-scroll
            setFocusSource(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusedId, focusSource, setFocusSource, items]);

    // Busy Life Presets - saved filter combinations
    const PRESETS = [
        { id: 'overdue', label: '\u{1F534} Overdue', params: { state: 'ACTIVE,TRIAGE', overdue: 'true' }, highlight: true },
        { id: 'bills-due', label: '\u{1F4B0} Bills Due Soon', params: { area: 'finance', state: 'ACTIVE,TRIAGE' } },
        { id: 'school-week', label: '\u{1F9F8} Kids This Week', params: { area: 'parenthood', state: 'ACTIVE,TRIAGE' } },
        { id: 'job-defense', label: '\u{1F4BC} Job Hunt', params: { view: 'applications', area: 'job_hunt', state: 'ACTIVE,TRIAGE' } },
        { id: 'business', label: '\u{1F3E2} Business', params: { area: 'business', state: 'ACTIVE,TRIAGE' } },
    ];

    const applyPreset = (preset: typeof PRESETS[0]) => {
        const next = new URLSearchParams();
        Object.entries(preset.params).forEach(([key, value]) => {
            next.set(key, value);
        });
        setSearchParams(next);
    };

    // Check if any NON-OBVIOUS filters are active.
    // View tab and state tab are already visually indicated in the header,
    // so we only show the breadcrumb for filters that aren't immediately visible
    // (area, entity, needs-action, search text).
    // Implicit active-domain default (no `area` in URL) is not treated as a user-applied filter.
    const hasActiveFilters = !!(
        (areaExplicitUserFilter && areaFilter !== null && areaFilter !== 'all') ||
        entityFilterFromUrl ||
        needsActionOnly ||
        overdueOnly ||
        search
    );

    const isFilteredEmpty = useMemo(
        () =>
            !!(areaExplicitUserFilter && areaFilter !== null && areaFilter !== 'all') ||
            !!entityFilterFromUrl ||
            needsActionOnly ||
            overdueOnly ||
            !!search ||
            stateFilter !== 'ACTIVE,TRIAGE' ||
            currentView !== 'all',
        [areaExplicitUserFilter, areaFilter, entityFilterFromUrl, needsActionOnly, overdueOnly, search, stateFilter, currentView],
    );

    const clearAllFilters = () => {
        if (searchUrlDebounceRef.current) {
            clearTimeout(searchUrlDebounceRef.current);
            searchUrlDebounceRef.current = null;
        }
        setSearch('');
        setSearchTotal(null);
        setSearchParams(new URLSearchParams());
    };

    const activeFiltersList = [
        { id: 'search', label: `Search: "${search}"`, active: !!search, clear: clearSearchUrlImmediate },
        { id: 'area', label: `Area: ${areas.find((a) => a.id === areaFilter ?? '')?.label}`, active: areaExplicitUserFilter && areaFilter !== null && areaFilter !== 'all', clear: () => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.set('area', 'all'); return dropDocumentFamilyScope(n); }) },
        { id: 'entity', label: `Entity: ${entityFilterFromUrl}`, active: !!entityFilterFromUrl, clear: () => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('entity'); return dropDocumentFamilyScope(n); }) },
        { id: 'needsAction', label: 'Needs Action Only', active: needsActionOnly, clear: () => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('needsAction'); return dropDocumentFamilyScope(n); }) },
        { id: 'overdue', label: 'Overdue Only', active: overdueOnly, clear: () => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('overdue'); return dropDocumentFamilyScope(n); }) },
        { id: 'state', label: `State: ${STATES.find((s) => s.id === stateFilter)?.label}`, active: stateFilter !== 'ACTIVE,TRIAGE', clear: () => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.set('state', 'ACTIVE,TRIAGE'); return dropDocumentFamilyScope(n); }) },
        { id: 'view', label: `View: ${VIEWS.find((v) => v.id === currentView)?.label}`, active: currentView !== 'all', clear: () => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('view'); return dropDocumentFamilyScope(n); }) },
    ].filter((f) => f.active);

    const handleViewChange = (viewId: ViewKey) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('view', viewId);
            return dropDocumentFamilyScope(next);
        });
    };

    const handleAreaChange = (areaId: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('area', areaId);
            return dropDocumentFamilyScope(next);
        });
    };

    const handleStateChange = (stateId: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('state', stateId);
            return dropDocumentFamilyScope(next);
        });
    };

    const handleToggleNeedsAction = () => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('needsAction', (!needsActionOnly).toString());
            return dropDocumentFamilyScope(next);
        });
    };

    const handleRetryFetch = () => {
        setRetryToken((prev) => prev + 1);
    };

    const isInitialLoading = (loading && page === 1) || (areaFilter === null && !sourceHomeId);
    const statusMessage = fetchError
        ? fetchError
        : loading
            ? (page === 1 ? 'Loading work items.' : 'Loading more work items.')
            : `${items.length} work item${items.length === 1 ? '' : 's'} loaded.`;

    const handleUndo = async () => {
        if (!lastAction) return;

        // If there's a pending timer, cancel it and revert UI
        if (pendingActionTimerRef.current) {
            clearTimeout(pendingActionTimerRef.current);
            pendingActionTimerRef.current = null;

            const revertedItems = items.map(it => {
                const prevState = lastAction.previousStates[it.id];
                if (prevState) {
                    return { ...it, state: prevState };
                }
                return it;
            });
            setItems(revertedItems);
            setToast({ message: 'Action cancelled.', severity: 'info' });
            setLastAction(null);
            refreshSanityCounts();
            return;
        }

        try {
            const promises = lastAction.items.map(item =>
                workItemsApi.updateWorkItem(item.id, { state: lastAction.previousStates[item.id] as any })
            );
            const results = await Promise.allSettled(promises);
            const succeededIds = results
                .map((res, idx) => (res.status === 'fulfilled' ? lastAction.items[idx].id : null))
                .filter((id): id is string => id !== null);

            if (succeededIds.length > 0) {
                setItems(prev => prev.map(it => {
                    if (succeededIds.includes(it.id)) {
                        return { ...it, state: lastAction.previousStates[it.id] as any };
                    }
                    return it;
                }));
                refreshSanityCounts();
                setToast({ message: `Restored ${succeededIds.length} items.`, severity: 'info' });
            }
            setLastAction(null);
        } catch (error) {
            setToast({ message: 'Failed to undo action.', severity: 'error' });
        }
    };

    return (
        <Box className="space-y-4 sm:space-y-6">
            <Box
                role="status"
                aria-live="polite"
                aria-atomic="true"
                sx={visuallyHiddenSx}
            >
                {statusMessage}
            </Box>
            <OrganizeGeneralWorkbenchEntry
                visible={
                    areaFilter === 'general' &&
                    bootstrap?.capabilities?.organize_general_enabled === true
                }
            />
            <SimilarItemWorkbenchEntry
                visible={bootstrap?.capabilities?.similar_item_suggestions_enabled === true}
                suggestionCount={similarItemSuggestionCount}
            />

            {showHeader && (
                <WorkItemsHeader
                    areas={areas}
                    areaFilter={areaFilter}
                    currentView={currentView}
                    stateFilter={stateFilter}
                    search={search}
                    needsActionOnly={needsActionOnly}
                    sanityCounts={sanityCounts}
                    views={views}
                    states={STATES}
                    onViewChange={handleViewChange}
                    onAreaChange={handleAreaChange}
                    onSearchChange={handleSearchChange}
                    onToggleNeedsAction={handleToggleNeedsAction}
                    onImportClick={() => setShowImportModal(true)}
                    onShortcutsClick={() => setShowShortcuts(true)}
                    onStateChange={handleStateChange}
                />
            )}

            {showHeader && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-theme bg-theme-secondary px-3 py-2 text-xs text-theme-secondary">
                    <label className="flex items-center gap-2 font-semibold">
                        <input
                            type="checkbox"
                            checked={includeRetiredModes}
                            onChange={(event) => handleIncludeRetiredModesChange(event.target.checked)}
                        />
                        Include archived modes
                    </label>
                </div>
            )}

            <WorkItemsMeta
                sanityCounts={sanityCounts}
                currentView={currentView}
                stateFilter={stateFilter}
                areaFilter={areaFilter ?? 'all'}
                areaFilterExplicit={areaExplicitUserFilter}
                areas={areas}
                views={views}
                states={STATES}
                entityFilter={entityFilterFromUrl}
                needsActionOnly={needsActionOnly}
                search={search}
                searchResultTotal={searchTotal}
                hasActiveFilters={hasActiveFilters}
                onClearAllFilters={clearAllFilters}
                onApplyOverduePreset={() => applyPreset(PRESETS[0])}
            />

            <WorkItemsTable
                userTimeZone={bootstrap?.user?.timezone}
                items={items}
                loading={loading}
                isInitialLoading={isInitialLoading}
                errorMessage={fetchError}
                onRetry={handleRetryFetch}
                onLoadMore={loadMore}
                domainConfigs={domainConfigs}
                currentView={currentView}
                actions={{
                    onOpenItem: openItem,
                    onQuickDone: quickDone,
                    onQuickArchive: quickArchiveOrDelete,
                    onQuickSnooze: quickSnooze,
                    onBulkDone: handleBulkDone,
                    onBulkArchive: bulkArchiveOrDelete,
                    onBulkSnooze: handleBulkSnooze,
                    onReclassify: handleReclassify,
                }}
                state={{
                    focusedId,
                    openDrawerId: selected?.id ?? null,
                    selectedIds,
                    onRowClick: handleItemClick,
                    onRowDoubleClick: handleItemDoubleClick,
                    onCheckboxClick: handleCheckboxClick,
                    onClearSelection: () => setSelectedIds(new Set()),
                }}
                virtuosoRef={virtuosoRef}
                activeFilters={activeFiltersList}
                onResetFilters={clearAllFilters}
                emptyStateVariant={isArchiveView ? (isFilteredEmpty ? 'filtered' : 'archive-empty') : isFilteredEmpty ? 'filtered' : 'true-empty'}
                mailboxConnected={bootstrap?.mailbox?.connected ?? false}
                onEmptyImportClick={() => setShowImportModal(true)}
                onEmptyConnectMailClick={() => openConnectGmailModal()}
                archiveAction={isArchiveView ? 'delete' : 'archive'}
            />

            <WorkItemDrawer
                open={Boolean(selected)}
                onClose={() => {
                    // Prevent same-tick auto-restore from immediately reopening drawer.
                    suppressAutoRestoreUntilRef.current = Date.now() + 1200;
                    try {
                        sessionStorage.removeItem(DRAWER_ITEM_CACHE_KEY);
                        sessionStorage.removeItem(LAST_FOCUS_ID_KEY);
                        sessionStorage.removeItem(LAST_FOCUS_ITEM_KEY);
                    } catch {
                        // Ignore sessionStorage failures.
                    }
                    setSelected(null);
                    setFocusIdParam(null);
                    restoreDrawerActivatorFocus();
                }}
                item={selected}
                onUpdate={handleItemUpdate}
                onDelete={handleItemDelete}
                navigation={drawerNavigationWithPile}
                onOpenFamilyMember={(member) => {
                    const known = items.find((row) => row.id === member.id);
                    if (known) {
                        openItem(known);
                        return;
                    }
                    setSelected(member);
                }}
            />

            <FileImportModal
                open={showImportModal}
                onClose={() => setShowImportModal(false)}
                onImport={handleImportItems}
            />

            <KeyboardShortcutsModal
                open={showShortcuts}
                onClose={() => setShowShortcuts(false)}
            />

            <ConfirmationDialog
                open={confirmDelete.open}
                title={confirmDelete.isBulk ? 'Bulk Delete Items' : 'Delete Item'}
                message={
                    confirmDelete.isBulk
                        ? `Are you sure you want to permanently delete ${confirmDelete.ids.length} selected items? This action cannot be undone.`
                        : 'Are you sure you want to permanently delete this item? This action cannot be undone.'
                }
                confirmLabel="Delete Permanently"
                isDestructive
                onConfirm={performDelete}
                onCancel={() => setConfirmDelete({ open: false, ids: [], isBulk: false })}
            />

            <Snackbar
                open={Boolean(toast)}
                autoHideDuration={6000}
                onClose={() => {
                    setToast(null);
                    setLastAction(null);
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {toast ? (
                    <Alert
                        onClose={() => setToast(null)}
                        severity={toast.severity as any}
                        variant="filled"
                        sx={{ width: '100%', borderRadius: '16px', fontWeight: 600 }}
                        action={
                            lastAction ? (
                                <Button
                                    onClick={handleUndo}
                                    variant="text"
                                    size="small"
                                    sx={{
                                        color: 'white',
                                        fontWeight: 800,
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        mr: 1,
                                        px: 1.5,
                                        minWidth: 'auto',
                                        bgcolor: 'rgba(255,255,255,0.15)',
                                        borderRadius: '8px',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                                    }}
                                >
                                    Undo
                                </Button>
                            ) : undefined
                        }
                    >
                        {toast.message}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </Box>
    );
};

export default WorkItemsView;
